import isEqual from 'lodash/isEqual';
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Field, FormSpy } from 'react-final-form';
import setFieldData from 'final-form-set-field-data';

import { AppIcon } from '@folio/stripes/core';
import {
  Paneset,
  Pane,
  PaneMenu,
  PaneHeaderIconButton,
  PaneFooter,
  Button,
  ExpandAllButton,
  Row,
  Col,
  Headline,
  AccordionSet,
  AccordionStatus,
  HasCommand,
  expandAllSections,
  collapseAllSections,
} from '@folio/stripes/components';
import { EditCustomFieldsRecord } from '@folio/stripes/smart-components';
import stripesFinalForm from '@folio/stripes/final-form';

import { USER_TYPES } from '../../constants';
import {
  EditUserInfo,
  EditExtendedInfo,
  EditContactInfo,
  EditServicePoints,
} from '../../components/EditSections';
import EditPasswordSection from '../../components/EditSections/EditPasswordSection';
import PermissionsAccordion from '../../components/PermissionsAccordion';
import {
  statusFilterConfig,
  permissionTypeFilterConfig,
} from '../../components/PermissionsAccordion/helpers/filtersConfig';
import { addressTypesShape } from '../../shapes';
import getProxySponsorWarning from '../../components/util/getProxySponsorWarning';
import { DefaultLibraryEditInfo } from '../../components/UserClearance';

import css from '../UserEdit/UserForm.css';

export function validate(values) {
  const errors = {};

  errors.personal = {};

  if (!values.personal || !values.personal.lastName) {
    errors.personal.lastName = (
      <FormattedMessage id="ui-users.errors.missingRequiredField" />
    );
  }

  if (!values.patronGroup) {
    errors.patronGroup = (
      <FormattedMessage id="ui-users.errors.missingRequiredPatronGroup" />
    );
  }

  if (!values.personal || !values.personal.preferredContactTypeId) {
    if (errors.personal)
      errors.personal.preferredContactTypeId = (
        <FormattedMessage id="ui-users.errors.missingRequiredContactType" />
      );
    else
      errors.personal = {
        preferredContactTypeId: (
          <FormattedMessage id="ui-users.errors.missingRequiredContactType" />
        ),
      };
  }

  if (values.personal && values.personal.addresses) {
    errors.personal.addresses = [];
    values.personal.addresses.forEach((addr) => {
      const err = !addr.addressType
        ? {
            addressType: (
              <FormattedMessage id="ui-users.errors.missingRequiredAddressType" />
            ),
          }
        : {};
      errors.personal.addresses.push(err);
    });
  }

  // Password validation
  if (!values.password) {
    errors.password = (
      <FormattedMessage id="ui-users.errors.missingRequiredField" />
    );
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = (
      <FormattedMessage id="ui-users.errors.missingRequiredField" />
    );
  }

  if (
    values.password &&
    values.confirmPassword &&
    values.password !== values.confirmPassword
  ) {
    errors.confirmPassword = (
      <FormattedMessage id="ui-users.errors.passwordMismatch" />
    );
  }

  /** kware start edit - Add service points validation for staff users */
  if (
    values.servicePoints &&
    values.servicePoints.length > 0 &&
    (!values.preferredServicePoint ||
      values.preferredServicePoint === '-' ||
      values.preferredServicePoint === '')
  ) {
    errors.preferredServicePoint = (
      <FormattedMessage id="ui-users.errors.missingRequiredPreferredServicePoint" />
    );
  }
  /** kware end edit */

  return errors;
}

class StaffUserForm extends React.Component {
  static propTypes = {
    change: PropTypes.func,
    addressTypes: addressTypesShape,
    formData: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    location: PropTypes.object,
    history: PropTypes.object,
    uniquenessValidator: PropTypes.shape({
      reset: PropTypes.func.isRequired,
      GET: PropTypes.func.isRequired,
    }).isRequired,
    match: PropTypes.object,
    pristine: PropTypes.bool,
    submitting: PropTypes.bool,
    invalid: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    initialValues: PropTypes.object.isRequired,
    servicePoints: PropTypes.object,
    stripes: PropTypes.object,
    form: PropTypes.object,
    intl: PropTypes.object,
    profilePictureConfig: PropTypes.object.isRequired,
  };

  static defaultProps = {
    pristine: false,
    submitting: false,
  };

  constructor(props) {
    super(props);

    this.accordionStatusRef = React.createRef();
    this.closeButton = React.createRef();
    this.saveButton = React.createRef();
    this.cancelButton = React.createRef();

    this.keyboardCommands = [
      {
        name: 'save',
        handler: this.handleSaveKeyCommand,
      },
      {
        name: 'ignoreEnter',
        handler: this.ignoreEnterKey,
        shortcut: 'enter',
      },
      {
        name: 'cancel',
        handler: this.handleCancel,
        shortcut: 'esc',
      },
      {
        name: 'expandAllSections',
        handler: this.handleExpandAll,
      },
      {
        name: 'collapseAllSections',
        handler: this.handleCollapseAll,
      },
    ];

    this.buttonRefs = [];
    this.setButtonRef = (el) => this.buttonRefs.push(el);
  }

  handleCollapseAll = (e) => collapseAllSections(e, this.accordionStatusRef);
  handleExpandAll = (e) => expandAllSections(e, this.accordionStatusRef);

  handleCancel = () => {
    const { history } = this.props;
    history.push('/users/');
  };

  getAddFirstMenu() {
    const { intl } = this.props;

    return (
      <PaneMenu>
        <PaneHeaderIconButton
          id="clickable-closenewstaffuserdialog"
          onClick={this.handleCancel}
          ref={this.closeButton}
          aria-label={intl.formatMessage({
            id: 'ui-users.crud.closeNewUserDialog',
          })}
          icon="times"
        />
      </PaneMenu>
    );
  }

  ignoreEnterKey = (e) => {
    const allowedControls = [
      this.closeButton.current,
      this.saveButton.current,
      this.cancelButton.current,
      ...this.buttonRefs,
    ];

    if (!allowedControls.includes(e.target)) {
      e.preventDefault();
    }
  };

  handleSaveKeyCommand = (e) => {
    e.preventDefault();
    this.executeSave();
  };

  executeSave() {
    const { handleSubmit, onSubmit } = this.props;
    const submitter = handleSubmit(onSubmit);
    submitter();
  }

  getPaneFooter() {
    const { pristine, submitting, onCancel } = this.props;
    const disabled = pristine || submitting;

    const startButton = (
      <Button
        data-test-staff-user-form-cancel-button
        marginBottom0
        id="clickable-cancel"
        buttonStyle="default mega"
        buttonRef={this.cancelButton}
        onClick={onCancel}
      >
        <FormattedMessage id="ui-users.cancel" />
      </Button>
    );

    const endButton = (
      <Button
        data-test-staff-user-form-submit-button
        marginBottom0
        id="clickable-save"
        buttonStyle="primary mega"
        type="submit"
        disabled={disabled}
        buttonRef={this.saveButton}
      >
        <FormattedMessage id="ui-users.saveAndClose" />
      </Button>
    );

    return (
      <PaneFooter
        renderStart={startButton}
        renderEnd={endButton}
        className={css.paneFooterClass}
        innerClassName={css.paneFooterContentClass}
      />
    );
  }

  render() {
    const {
      initialValues,
      handleSubmit,
      formData,
      servicePoints,
      onCancel,
      stripes,
      form,
      uniquenessValidator,
      profilePictureConfig,
    } = this.props;

    const selectedPatronGroup = form.getFieldState('patronGroup')?.value;
    const firstMenu = this.getAddFirstMenu();
    const footer = this.getPaneFooter();
    const paneTitle = <FormattedMessage id="ui-users.crud.createStaffUser" />;

    return (
      <HasCommand commands={this.keyboardCommands}>
        <form
          data-test-staff-form-page
          className={css.UserFormRoot}
          id="form-staff-user"
          onSubmit={handleSubmit}
        >
          <Paneset>
            <Pane
              firstMenu={firstMenu}
              footer={footer}
              centerContent
              appIcon={<AppIcon app="users" appIconKey="users" />}
              paneTitle={<span data-test-header-title>{paneTitle}</span>}
              onClose={onCancel}
              defaultWidth="fill"
            >
              <AccordionStatus ref={this.accordionStatusRef}>
                <Row end="xs">
                  <Col xs>
                    <ExpandAllButton />
                  </Col>
                </Row>
                <AccordionSet
                  initialStatus={{
                    editUserInfo: true,
                    extendedInfo: true,
                    contactInfo: true,
                    passwordSection: true,
                    permissions: true,
                    servicePoints: true,
                    /* kware start edit - Hide customFields accordion since EditCustomFieldsRecord is commented out
                       and replaced with DefaultLibraryEditInfo component */
                    // customFields: true,
                    /* kware end edit */
                    defaultLibraryInfo: true,
                  }}
                >
                  <EditUserInfo
                    accordionId="editUserInfo"
                    initialValues={{ ...initialValues, type: USER_TYPES.STAFF }}
                    patronGroups={formData.patronGroups}
                    stripes={stripes}
                    form={form}
                    selectedPatronGroup={selectedPatronGroup}
                    uniquenessValidator={uniquenessValidator}
                    disabled={false}
                    profilePictureConfig={profilePictureConfig}
                  />
                  <EditExtendedInfo
                    accordionId="extendedInfo"
                    userId={null}
                    userFirstName={initialValues.personal?.firstName}
                    userEmail={initialValues.personal?.email}
                    username={initialValues.username}
                    servicePoints={servicePoints}
                    addressTypes={formData.addressTypes}
                    departments={formData.departments}
                    uniquenessValidator={uniquenessValidator}
                    disabled={false}
                    stripes={stripes}
                  />
                  <EditContactInfo
                    accordionId="contactInfo"
                    addressTypes={formData.addressTypes}
                    preferredContactTypeId={
                      initialValues.preferredContactTypeId
                    }
                    disabled={false}
                  />
                  <EditPasswordSection
                    accordionId="passwordSection"
                    disabled={false}
                  />
                  <PermissionsAccordion
                    accordionId="permissions"
                    initialValues={initialValues}
                    filtersConfig={[
                      permissionTypeFilterConfig,
                      statusFilterConfig,
                    ]}
                    visibleColumns={[
                      'selected',
                      'permissionName',
                      'type',
                      'status',
                    ]}
                    headlineContent={
                      <FormattedMessage id="ui-users.permissions.userPermissions" />
                    }
                    permToRead="perms.users.get"
                    permToDelete="perms.users.item.delete"
                    permToModify="perms.users.item.put"
                    formName="staffUserForm"
                    permissionsField="permissions"
                    form={form}
                    setButtonRef={this.setButtonRef}
                    tenantId={stripes.okapi.tenant}
                  />
                  {/* kware start edit - Add service points section for staff users */}
                  <EditServicePoints
                    accordionId="servicePoints"
                    setButtonRef={this.setButtonRef}
                    initialValues={initialValues}
                    formData={formData}
                    form={form}
                    intl={this.props.intl}
                    {...this.props}
                  />
                  {/* kware end edit */}
                  {/* kware start edit - Hide EditCustomFieldsRecord to avoid duplicate clearance sections
                      We now use DefaultLibraryEditInfo component which provides better control.
                      The EditCustomFieldsRecord is kept commented for potential future use of other custom fields.
                  <EditCustomFieldsRecord
                    formName="staffUserForm"
                    accordionId="customFields"
                    backendModuleName="users"
                    entityType="user"
                    finalFormCustomFieldsValues={
                      form.getState().values.customFields
                    }
                    fieldComponent={Field}
                    changeFinalFormField={form.change}
                  />
                  kware end edit */}
                  {/* kware start edit - Add default library info for staff users */}
                  <DefaultLibraryEditInfo
                    accordionId="defaultLibraryInfo"
                    expanded={true}
                    onToggle={() => {}}
                    libraries={formData.libraries || []}
                    disabled={false}
                  />
                  {/* kware end edit */}
                </AccordionSet>
              </AccordionStatus>
            </Pane>
          </Paneset>
          <FormSpy
            subscription={{ values: true }}
            onChange={({ values }) => {
              const { mutators } = form;

              ['sponsors', 'proxies'].forEach((namespace) => {
                if (values[namespace]) {
                  values[namespace].forEach((_, index) => {
                    const warning = getProxySponsorWarning(
                      values,
                      namespace,
                      index,
                    );

                    if (warning) {
                      mutators.setFieldData(
                        `${namespace}[${index}].proxy.status`,
                        { warning },
                      );
                    }
                  });
                }
              });
            }}
          />
        </form>
      </HasCommand>
    );
  }
}

export default stripesFinalForm({
  validate,
  mutators: {
    setFieldData,
  },
  initialValuesEqual: (a, b) => isEqual(a, b),
  navigationCheck: true,
  enableReinitialize: true,
})(injectIntl(StaffUserForm));
